import { z } from 'zod';
import { QUALITY_RISK_SEVERITIES } from './quality-hq';

/**
 * `pending-project-knowledge` v0 — glossary CRUD + compiled snapshot.
 *
 * Compile is deterministic (no LLM). The snapshot is a markdown block
 * capped at `knowledge.maxTokens` (default 2000) and attached to
 * existing parse / generate prompts. See
 * `docs/handbook/features/pending/project-knowledge.md`.
 */

export const GlossaryTermSchema = z.object({
  term: z.string().trim().min(1).max(80),
  synonyms: z.array(z.string().trim().min(1).max(80)).max(20).default([]),
  uiHint: z.string().trim().max(200).optional(),
  notes: z.string().trim().max(500).optional(),
});
export type GlossaryTerm = z.infer<typeof GlossaryTermSchema>;

export const UpsertGlossaryRequestSchema = z.object({
  terms: z.array(GlossaryTermSchema).min(0).max(200),
});
export type UpsertGlossaryRequest = z.infer<typeof UpsertGlossaryRequestSchema>;

export const GlossaryListResponseSchema = z.object({
  terms: z.array(
    GlossaryTermSchema.extend({
      updatedAt: z.string().optional(),
    }),
  ),
  maxTerms: z.number().int().min(1),
});
export type GlossaryListResponse = z.infer<typeof GlossaryListResponseSchema>;

export const KnowledgeSnapshotSourcesSchema = z.object({
  glossary: z.number().int().min(0),
  components: z.number().int().min(0),
  presets: z.number().int().min(0),
  flows: z.number().int().min(0),
  acs: z.number().int().min(0),
  openapi: z.number().int().min(0),
  journeys: z.number().int().min(0).default(0),
  personas: z.number().int().min(0).default(0),
  rules: z.number().int().min(0).default(0),
  constraints: z.number().int().min(0).default(0),
  /** Confirmed discovered business capabilities (`pending-knowledge-hub` v0). */
  capabilities: z.number().int().min(0).default(0),
});
export type KnowledgeSnapshotSources = z.infer<typeof KnowledgeSnapshotSourcesSchema>;

export const KnowledgeSnapshotSchema = z.object({
  projectId: z.string().uuid(),
  compiledAt: z.string(),
  tokenCount: z.number().int().min(0),
  text: z.string(),
  sources: KnowledgeSnapshotSourcesSchema,
});
export type KnowledgeSnapshot = z.infer<typeof KnowledgeSnapshotSchema>;

// ---- v1 knowledge entries (journeys / personas / rules / constraints) ----

export const KnowledgeEntryKindSchema = z.enum(['journey', 'persona', 'rule', 'constraint']);
export type KnowledgeEntryKind = z.infer<typeof KnowledgeEntryKindSchema>;

export const KnowledgeEntrySchema = z.object({
  id: z.string().uuid().optional(),
  kind: KnowledgeEntryKindSchema,
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().max(1000).default(''),
  sortOrder: z.number().int().min(0).max(999).optional(),
  updatedAt: z.string().optional(),
});
export type KnowledgeEntry = z.infer<typeof KnowledgeEntrySchema>;

export const UpsertKnowledgeEntriesRequestSchema = z.object({
  entries: z.array(KnowledgeEntrySchema).min(0).max(40),
});
export type UpsertKnowledgeEntriesRequest = z.infer<typeof UpsertKnowledgeEntriesRequestSchema>;

export const KnowledgeEntryListResponseSchema = z.object({
  entries: z.array(KnowledgeEntrySchema),
  maxEntries: z.number().int().min(1),
});
export type KnowledgeEntryListResponse = z.infer<typeof KnowledgeEntryListResponseSchema>;

// ---- v1.1 glossary proposals + chip grounding ----

export const GlossaryProposalSourceSchema = z.enum(['flow-title', 'promoted-selector', 'describe-label']);
export type GlossaryProposalSource = z.infer<typeof GlossaryProposalSourceSchema>;

export const GlossaryProposalSchema = GlossaryTermSchema.extend({
  source: GlossaryProposalSourceSchema,
});
export type GlossaryProposal = z.infer<typeof GlossaryProposalSchema>;

export const GlossaryProposeResponseSchema = z.object({
  proposals: z.array(GlossaryProposalSchema),
});
export type GlossaryProposeResponse = z.infer<typeof GlossaryProposeResponseSchema>;

export interface GlossaryGroundTerm {
  term: string;
  synonyms: string[];
  uiHint?: string;
}

export const GLOSSARY_PROPOSE_MAX = 10;

// ---- Knowledge Hub v0 (`pending-knowledge-hub`) — discover / review ----

/** Source tables already in Rynora. Git AST / prod traces land in later slices. */
export const KnowledgeSourceKindSchema = z.enum(['flow', 'risk', 'story', 'openapi', 'finding', 'manual', 'repo']);
export type KnowledgeSourceKind = z.infer<typeof KnowledgeSourceKindSchema>;

export const KnowledgeDiscoveryStatusSchema = z.enum(['proposed', 'confirmed', 'dismissed']);
export type KnowledgeDiscoveryStatus = z.infer<typeof KnowledgeDiscoveryStatusSchema>;

export const KnowledgeEvidenceGapSchema = z.enum(['production', 'documentation', 'api', 'tests']);
export type KnowledgeEvidenceGap = z.infer<typeof KnowledgeEvidenceGapSchema>;

export const KnowledgeEvidenceTickSchema = z.object({
  kind: KnowledgeSourceKindSchema,
  count: z.number().int().min(0),
});
export type KnowledgeEvidenceTick = z.infer<typeof KnowledgeEvidenceTickSchema>;

export const KnowledgeDiscoveryEvidenceSchema = z.object({
  ticks: z.array(KnowledgeEvidenceTickSchema),
  missing: z.array(KnowledgeEvidenceGapSchema),
  highRevenue: z.boolean().default(false),
  /**
   * Wave 3C — honesty band for production/execution evidence.
   * AVAILABLE = bounded evidence exists (not “production healthy”).
   */
  productionEvidenceStatus: z.enum(['available', 'limited', 'insufficient_evidence', 'unavailable']).optional(),
  productionEvidenceSummary: z.string().max(500).optional(),
});
export type KnowledgeDiscoveryEvidence = z.infer<typeof KnowledgeDiscoveryEvidenceSchema>;

export const KnowledgeDiscoverySchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  slug: z.string().min(1).max(64),
  title: z.string().min(1).max(120),
  status: KnowledgeDiscoveryStatusSchema,
  confidence: z.number().min(0).max(1),
  evidence: KnowledgeDiscoveryEvidenceSchema,
  variants: z.array(z.string().min(1).max(160)).max(20),
  flowIds: z.array(z.string().uuid()),
  sourceKinds: z.array(KnowledgeSourceKindSchema),
  updatedAt: z.string(),
});
export type KnowledgeDiscovery = z.infer<typeof KnowledgeDiscoverySchema>;

export const KnowledgeDiscoveryListResponseSchema = z.object({
  discoveries: z.array(KnowledgeDiscoverySchema),
  proposedCount: z.number().int().min(0),
  unknownCount: z.number().int().min(0),
});
export type KnowledgeDiscoveryListResponse = z.infer<typeof KnowledgeDiscoveryListResponseSchema>;

export const ReviewKnowledgeDiscoveryRequestSchema = z.object({
  status: z.enum(['confirmed', 'dismissed']),
});
export type ReviewKnowledgeDiscoveryRequest = z.infer<typeof ReviewKnowledgeDiscoveryRequestSchema>;

/** Proposed discoveries at or above this score can be confirmed in bulk. */
export const KNOWLEDGE_DISCOVERY_CONFIRM_ALL_MIN = 0.75;
/** Clusters below this never persist — a single untitled flow is not a capability. */
export const KNOWLEDGE_DISCOVERY_MIN_CONFIDENCE = 0.35;

/** True when a chip target looks like a CSS / testid locator, not a product phrase. */
export function looksLikeLocatorToken(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (/[.#\[\]=_]/.test(t)) return true;
  if (/-/.test(t) && !/\s/.test(t)) return true;
  return /^(button|input|a|div|span|select|textarea|img|label|nav|svg)[\s.#\[]/i.test(t);
}

export function normalizeGlossaryToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[.#\[\]"'=]+/g, ' ')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Rewrite suggestion-chip labels through the product glossary so the
 * strip reads `Click "basket"` instead of `Click "button.cart-icon"`.
 * Inverse of parse-time synonym → ui_hint rewrite.
 */
export function groundChipLabel(label: string, terms: GlossaryGroundTerm[]): string {
  if (!label || terms.length === 0) return label;
  return label.replace(
    /"([^"]+)"|'([^']+)'|([#.][A-Za-z][\w-]*)|(\b[A-Za-z][\w.-]*\b)/g,
    (full, dq: string | undefined, sq: string | undefined, css: string | undefined, bare: string | undefined) => {
      const token = dq || sq || css || bare;
      if (!token) return full;
      const hit = matchGlossaryForChipToken(token, terms);
      if (!hit) return full;
      if (dq) return `"${hit}"`;
      if (sq) return `'${hit}'`;
      return hit;
    },
  );
}

/** Journey / constraint / rule text used to ground Agent + exploratory. */
export interface KnowledgeGroundHint {
  title: string;
  body?: string;
}

export interface KnowledgeExploratoryGround {
  journeys: KnowledgeGroundHint[];
  constraints: KnowledgeGroundHint[];
  highRevenueUrls: string[];
}

export const EMPTY_KNOWLEDGE_GROUND: KnowledgeExploratoryGround = {
  journeys: [],
  constraints: [],
  highRevenueUrls: [],
};

const KNOWLEDGE_PATH_TOKEN_RE = /\/[a-z0-9/_-]{2,}/gi;
const KNOWLEDGE_STOP_WORDS = new Set([
  'do',
  'not',
  'never',
  'dont',
  'must',
  'should',
  'avoid',
  'skip',
  'from',
  'this',
  'that',
  'with',
  'page',
  'form',
  'user',
  'the',
  'and',
  'for',
  'please',
  'always',
  'into',
  'onto',
]);

export function knowledgeHintBlob(hint: KnowledgeGroundHint): string {
  return `${hint.title} ${hint.body || ''}`.trim().toLowerCase();
}

export function urlPathHint(url: string): string {
  try {
    const path = new URL(url).pathname.toLowerCase();
    return path.length > 1 ? path.replace(/\/+$/, '') || '/' : path || '/';
  } catch {
    const raw = url.toLowerCase().split('?')[0] || url.toLowerCase();
    if (raw.startsWith('/')) return raw.replace(/\/+$/, '') || '/';
    const slash = raw.indexOf('/');
    return slash >= 0 ? raw.slice(slash).replace(/\/+$/, '') || '/' : raw;
  }
}

export function urlsSharePathHint(a: string, b: string): boolean {
  const pa = urlPathHint(a);
  const pb = urlPathHint(b);
  if (!pa || !pb) return false;
  return pa === pb || pa.includes(pb) || pb.includes(pa);
}

export function urlMatchesKnowledgeHint(url: string, hints: readonly KnowledgeGroundHint[]): boolean {
  if (!hints.length) return false;
  const urlLower = url.toLowerCase();
  const path = urlPathHint(url);
  for (const hint of hints) {
    const blob = knowledgeHintBlob(hint);
    if (!blob) continue;
    const paths = blob.match(KNOWLEDGE_PATH_TOKEN_RE) ?? [];
    if (paths.some((p) => path.includes(p) || urlLower.includes(p))) return true;
    const words = blob.split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !KNOWLEDGE_STOP_WORDS.has(w));
    if (words.some((w) => path.includes(w) || urlLower.includes(w))) return true;
  }
  return false;
}

export function constraintBlocksHaystack(haystack: string, constraints: readonly KnowledgeGroundHint[]): boolean {
  const h = haystack.toLowerCase();
  if (!h.trim() || constraints.length === 0) return false;
  for (const c of constraints) {
    const blob = knowledgeHintBlob(c);
    const words = blob.split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !KNOWLEDGE_STOP_WORDS.has(w));
    if (words.some((w) => h.includes(w))) return true;
  }
  return false;
}

export function knowledgeScoreForUrl(
  url: string,
  ground: Pick<KnowledgeExploratoryGround, 'journeys' | 'constraints' | 'highRevenueUrls'>,
): number {
  if (urlMatchesKnowledgeHint(url, ground.constraints)) return -100;
  let score = 0;
  if (urlMatchesKnowledgeHint(url, ground.journeys)) score += 30;
  if ((ground.highRevenueUrls || []).some((h) => urlsSharePathHint(url, h))) score += 20;
  return score;
}

export function rankUncoveredUrlsForAgent<T extends { url: string }>(
  items: readonly T[],
  ground: Pick<KnowledgeExploratoryGround, 'journeys' | 'constraints' | 'highRevenueUrls'>,
): T[] {
  return [...items].sort((a, b) => knowledgeScoreForUrl(b.url, ground) - knowledgeScoreForUrl(a.url, ground));
}

function matchGlossaryForChipToken(raw: string, terms: GlossaryGroundTerm[]): string | null {
  const n = normalizeGlossaryToken(raw);
  if (!n) return null;
  const selector = looksLikeLocatorToken(raw);
  for (const t of terms) {
    const termNorm = normalizeGlossaryToken(t.term);
    if (!termNorm || n === termNorm) continue;
    const aliases = [t.term, ...t.synonyms, t.uiHint || '']
      .map((s) => normalizeGlossaryToken(s))
      .filter((s) => s.length >= 2);
    for (const alias of aliases) {
      if (n === alias) return t.term;
      if (!selector) continue;
      const aliasToks = alias.split(' ').filter((x) => x.length >= 3);
      if (aliasToks.length === 0) continue;
      const nToks = new Set(n.split(' ').filter(Boolean));
      if (aliasToks.every((x) => nToks.has(x))) return t.term;
    }
  }
  return null;
}

// ---- Knowledge Overview compose (A/B only — no invented coverage / stack) ----

/**
 * Authored business-flow row for Knowledge Overview preview and the
 * dedicated Business Flows collection. Scenario count is composed from
 * quality_scenarios.business_flow_id — never invented.
 */
export const KnowledgeFlowRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  moduleId: z.string().uuid(),
  moduleName: z.string(),
  domainId: z.string().uuid(),
  domainName: z.string(),
  criticality: z.enum(QUALITY_RISK_SEVERITIES).nullable(),
  scenarioCount: z.number().int().nonnegative(),
  updatedAt: z.string().nullable(),
});
export type KnowledgeFlowRow = z.infer<typeof KnowledgeFlowRowSchema>;

export const KnowledgeModuleRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  domainId: z.string().uuid(),
  domainName: z.string(),
  flowCount: z.number().int().nonnegative(),
});
export type KnowledgeModuleRow = z.infer<typeof KnowledgeModuleRowSchema>;

/**
 * Bounded Application Structure preview for Knowledge Overview.
 * Authored Domain → Module → Business Flow hierarchy only — never
 * technical architecture nodes/edges. Scenario counts are composed;
 * individual scenarios are never expanded on Overview.
 */
export const KnowledgeStructureFlowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  scenarioCount: z.number().int().nonnegative(),
});
export type KnowledgeStructureFlow = z.infer<typeof KnowledgeStructureFlowSchema>;

export const KnowledgeStructureModuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Total non-archived flows under this module (not just preview length). */
  flowCount: z.number().int().nonnegative(),
  flows: z.array(KnowledgeStructureFlowSchema).max(5),
  truncatedFlows: z.boolean(),
});
export type KnowledgeStructureModule = z.infer<typeof KnowledgeStructureModuleSchema>;

export const KnowledgeStructureDomainSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Total non-archived modules under this domain. */
  moduleCount: z.number().int().nonnegative(),
  modules: z.array(KnowledgeStructureModuleSchema).max(5),
  truncatedModules: z.boolean(),
});
export type KnowledgeStructureDomain = z.infer<typeof KnowledgeStructureDomainSchema>;

export const KnowledgeStructurePreviewSchema = z.object({
  domainCount: z.number().int().nonnegative(),
  domains: z.array(KnowledgeStructureDomainSchema).max(8),
  truncatedDomains: z.boolean(),
});
export type KnowledgeStructurePreview = z.infer<typeof KnowledgeStructurePreviewSchema>;

/**
 * Knowledge Overview payload.
 * Language/framework/DB/deployment and Analysis Coverage % remain unavailable.
 * Pages/routes and API endpoint counts are proposal-backed (null until Analyze has run).
 */
export const KnowledgeOverviewResponseSchema = z.object({
  projectId: z.string().uuid(),
  application: z.object({
    name: z.string(),
    description: z.string(),
    /** First linked source-repo URL when present; otherwise null. */
    repositoryUrl: z.string().nullable(),
  }),
  /** Snapshot compile time — not an application-analysis job timestamp. */
  compiledAt: z.string().nullable(),
  /** Last completed Analyze Application run, when any. */
  lastAnalyzeCompletedAt: z.string().nullable().optional(),
  counts: z.object({
    businessFlows: z.number().int().nonnegative(),
    coreModules: z.number().int().nonnegative(),
    /** Confirmed capability discoveries — not a Feature entity. */
    confirmedCapabilities: z.number().int().nonnegative(),
    /**
     * Repo route proposals (proposed+confirmed). Null when Analyze Application
     * has never completed for this project.
     */
    pagesRoutes: z.number().int().nonnegative().nullable().optional(),
    /** Repo API proposals (proposed+confirmed). Null until Analyze has completed. */
    apiEndpoints: z.number().int().nonnegative().nullable().optional(),
    /**
     * Measured signal coverage from last Analyze: files with proposals/evidence
     * over sniffed files in the evidence pack. Null until Analyze has completed.
     */
    analysisCoveragePercent: z.number().int().min(0).max(100).nullable().optional(),
  }),
  /** Authored Domain → Module → Flow tree (bounded). */
  structurePreview: KnowledgeStructurePreviewSchema,
  /** Small preview only — never the full flow set. */
  flowPreview: z.array(KnowledgeFlowRowSchema).max(8),
  modulePreview: z.array(KnowledgeModuleRowSchema).max(8),
  /**
   * Criticality histogram over all non-archived flows (authored values only).
   * Null criticality counted under `unscored`.
   */
  criticalityCounts: z.object({
    critical: z.number().int().nonnegative(),
    high: z.number().int().nonnegative(),
    medium: z.number().int().nonnegative(),
    low: z.number().int().nonnegative(),
    unscored: z.number().int().nonnegative(),
  }),
  /**
   * Latest AI Analyze advisory Understanding (proposed only — not authored truth).
   * Null when Analyze has never produced an AI row.
   */
  aiAdvisory: z
    .object({
      status: z.enum(['proposed', 'partial', 'failed', 'skipped', 'applied']),
      applicationSummary: z.string().nullable(),
      primaryLanguage: z.string().nullable(),
      framework: z.string().nullable(),
      databases: z.array(z.string()),
      proposedDomains: z.array(
        z.object({
          name: z.string(),
          description: z.string().optional().default(''),
          modules: z
            .array(
              z.object({
                name: z.string(),
                description: z.string().optional().default(''),
                flows: z
                  .array(z.object({ name: z.string(), description: z.string().optional().default('') }))
                  .optional()
                  .default([]),
              }),
            )
            .optional()
            .default([]),
        }),
      ),
      proposedCapabilities: z.array(z.string()),
      evidenceRefs: z.array(z.string()),
      provider: z.string().nullable(),
      model: z.string().nullable(),
      skipReason: z.string().nullable().optional(),
      createdAt: z.string(),
      /** True when Analyze auto-authored Domain→Module→Flow from AI proposals. */
      applied: z.boolean().optional(),
    })
    .nullable()
    .optional(),
});
export type KnowledgeOverviewResponse = z.infer<typeof KnowledgeOverviewResponseSchema>;

export const KnowledgeBusinessFlowsQuerySchema = z.object({
  q: z.string().trim().max(200).optional().default(''),
  criticality: z
    .enum([...QUALITY_RISK_SEVERITIES, 'unscored', 'all'] as const)
    .optional()
    .default('all'),
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
});
export type KnowledgeBusinessFlowsQuery = z.infer<typeof KnowledgeBusinessFlowsQuerySchema>;

export const KnowledgeBusinessFlowsResponseSchema = z.object({
  projectId: z.string().uuid(),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  criticalityCounts: KnowledgeOverviewResponseSchema.shape.criticalityCounts,
  flows: z.array(KnowledgeFlowRowSchema),
});
export type KnowledgeBusinessFlowsResponse = z.infer<typeof KnowledgeBusinessFlowsResponseSchema>;

export const KnowledgeModulesQuerySchema = z.object({
  q: z.string().trim().max(200).optional().default(''),
  page: z.coerce.number().int().min(1).optional().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
});
export type KnowledgeModulesQuery = z.infer<typeof KnowledgeModulesQuerySchema>;

export const KnowledgeModulesResponseSchema = z.object({
  projectId: z.string().uuid(),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(100),
  modules: z.array(KnowledgeModuleRowSchema),
});
export type KnowledgeModulesResponse = z.infer<typeof KnowledgeModulesResponseSchema>;
